
import React, { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import BackToTopButton from '../../components/base/BackToTopButton';
import CookieBanner from '../../components/base/CookieBanner';
import SEOHead from '../../components/seo/SEOHead';

export default function Empresa() {
  const [yearsCount, setYearsCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);

  useEffect(() => {
    // Animate years counter
    const yearsInterval = setInterval(() => {
      setYearsCount(prev => {
        if (prev < 15) return prev + 1;
        clearInterval(yearsInterval);
        return 15;
      });
    }, 100);

    // Animate projects counter
    const projectsInterval = setInterval(() => {
      setProjectsCount(prev => {
        if (prev < 500) return prev + 25;
        clearInterval(projectsInterval);
        return 500;
      });
    }, 50);

    return () => {
      clearInterval(yearsInterval);
      clearInterval(projectsInterval);
    };
  }, []);

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "PintoLot",
      "description": "Empresa de pintura profesional en Olot con más de 15 años de experiencia",
      "foundingDate": "2008",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Olot",
        "addressRegion": "Girona",
        "addressCountry": "ES"
      },
      "areaServed": ["Olot", "La Garrotxa", "Girona"],
      "serviceType": ["Pintura interior", "Pintura exterior", "Pintura comercial", "Pintura industrial"],
      "telephone": "+34609936590",
      "email": "info@pintolot.com.es"
    }
  };

  return (
    <>
      <SEOHead
        title="Empresa de Pintura Profesional en Olot | PintoLot - 15 años experiencia"
        description="Conoce PintoLot, empresa de pintura profesional en Olot con más de 15 años de experiencia. Servicios para particulares y empresas en Garrotxa y Girona."
        keywords="empresa pintura Olot, pintores profesionales Garrotxa, historia PintoLot, experiencia pintura Girona"
        canonical={`${import.meta.env.VITE_SITE_URL}/empresa`}
        ogTitle="PintoLot - Empresa de Pintura Profesional en Olot y Garrotxa"
        ogDescription="Más de 15 años ofreciendo servicios de pintura profesional en Olot y la Garrotxa. Conoce nuestra historia, valores y equipo profesional."
        schema={seoSchema}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Empresa de pintura profesional en Olot y la Garrotxa
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Con más de 15 años de experiencia, somos la empresa de referencia en servicios de pintura para particulares y empresas en Girona y toda la comarca de la Garrotxa.
            </p>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Nuestra historia y compromiso
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  PINTOLOT nació en Olot con la misión de ofrecer servicios de pintura de máxima calidad a particulares y empresas de la Garrotxa. Desde nuestros inicios, hemos mantenido un compromiso inquebrantable con la excelencia, la puntualidad y la satisfacción de nuestros clientes.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  Nuestra empresa se ha especializado en brindar soluciones integrales de pintura, adaptándonos a las necesidades específicas de cada proyecto. Trabajamos tanto en viviendas particulares como en espacios comerciales e industriales, siempre con los más altos estándares de calidad.
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  A lo largo de estos años, hemos ampliado nuestra zona de actuación para cubrir toda la provincia de Girona, manteniendo siempre nuestras raíces en Olot y ofreciendo un servicio cercano y personalizado a cada cliente.
                </p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#51b038] mb-2">{yearsCount}+</div>
                    <div className="text-gray-600">Años de experiencia</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#51b038] mb-2">{projectsCount}+</div>
                    <div className="text-gray-600">Proyectos completados</div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/db5f4385c2b004e5d0f250a84dc13526.jpg"
                  alt="Equipo de PintoLot trabajando en Olot"
                  className="rounded-lg shadow-xl object-cover w-full h-96 lg:h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nuestros valores y principios
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Los valores que nos definen como empresa líder en servicios de pintura en la Garrotxa y provincia de Girona.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-award-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Calidad excepcional</h3>
                <p className="text-gray-600">
                  Utilizamos únicamente materiales de primera calidad y aplicamos las mejores técnicas de pintura para garantizar resultados duraderos y acabados perfectos en cada proyecto.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-heart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Compromiso personal</h3>
                <p className="text-gray-600">
                  Cada proyecto es único y especial para nosotros. Nos comprometemos personalmente con la satisfacción de nuestros clientes, cuidando cada detalle desde el primer contacto hasta la finalización.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Puntualidad</h3>
                <p className="text-gray-600">
                  Respetamos los plazos acordados y planificamos meticulosamente cada proyecto. Tu tiempo es valioso y nos comprometemos a cumplir con todas las fechas establecidas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nuestro equipo profesional
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Contamos con un equipo de pintores profesionales altamente cualificados, con amplia experiencia en todo tipo de proyectos de pintura en Olot, Girona y la Garrotxa.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Profesionales especializados
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Nuestro equipo está formado por pintores especializados en diferentes técnicas y tipos de pintura. Cada miembro del equipo cuenta con formación específica y años de experiencia en el sector, lo que nos permite ofrecer servicios de la más alta calidad.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Pintores certificados con más de 10 años de experiencia</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Especialistas en técnicas decorativas y acabados especiales</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Formación continua en nuevas técnicas y materiales</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#51b038] text-xl mr-3"></i>
                    <span className="text-gray-700">Conocimiento profundo del mercado local de Girona</span>
                  </div>
                </div>

                <p className="text-lg text-gray-600 mt-6">
                  Todos nuestros profesionales están asegurados y cuentan con las certificaciones necesarias para trabajar tanto en proyectos residenciales como comerciales e industriales en toda la provincia de Girona.
                </p>
              </div>

              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20painting%20team%20group%20photo%2C%20experienced%20painters%20in%20clean%20uniforms%20holding%20painting%20tools%2C%20modern%20workshop%20background%2C%20professional%20lighting%2C%20high%20quality%20photography&width=600&height=800&seq=professional-team&orientation=portrait"
                  alt="Equipo profesional de pintores en Olot"
                  className="rounded-lg shadow-xl object-cover w-full h-96 lg:h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Certificaciones y garantías
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Nuestra empresa cuenta con todas las certificaciones necesarias y ofrecemos garantías completas en todos nuestros trabajos de pintura.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Empresa registrada</h3>
                <p className="text-gray-600 text-sm">
                  Registrada oficialmente en Girona con todos los permisos y licencias requeridas.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-shield-star-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Seguro de responsabilidad</h3>
                <p className="text-gray-600 text-sm">
                  Cobertura completa de responsabilidad civil para todos nuestros trabajos.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-medal-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certificaciones</h3>
                <p className="text-gray-600 text-sm">
                  Certificados por las principales marcas de pintura y acabados profesionales.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-customer-service-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Garantía extendida</h3>
                <p className="text-gray-600 text-sm">
                  Garantía de hasta 5 años en materiales y mano de obra según el tipo de proyecto.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-[#51b038]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Quieres conocer más sobre nuestra empresa?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Estaremos encantados de contarte más sobre nuestros servicios y cómo podemos ayudarte con tu proyecto de pintura en Olot y la Garrotxa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contacto" 
                className="bg-white text-[#51b038] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Contactar ahora
              </a>
              <a 
                href="/servicios" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#51b038] px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver nuestros servicios
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTopButton />
      <CookieBanner />
    </>
  );
}
